import { useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import AddIcon from "@mui/icons-material/AddOutlined";
import { DataTable, type Column } from "@/components/data/data-table";
import { MonoText } from "@/components/display/mono";
import { StatusChip } from "@/components/display/status-chip";
import { RelativeTime } from "@/components/display/relative-time";
import { CodeBlock } from "@/components/display/code-block";
import { EmptyState } from "@/components/feedback/states";
import { useToast } from "@/components/feedback/toaster";
import { useConfirm } from "@/components/feedback/confirm-dialog";
import { type ApiKey, useApiKeyMutations, useApiKeys } from "@/api/endpoints";

export const Route = createFileRoute("/_app/endpoints/keys")({
  component: ApiKeysTab,
});

function ApiKeysTab() {
  const toast = useToast();
  const confirm = useConfirm();
  const { data, isLoading, error, refetch } = useApiKeys();
  const m = useApiKeyMutations();
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [scopes, setScopes] = useState("jobs:run,ingest:write");
  const [issued, setIssued] = useState<string | null>(null);

  const columns: Column<ApiKey>[] = [
    { id: "name", header: "Name", cell: (r) => r.name, sortValue: (r) => r.name },
    {
      id: "prefix",
      header: "Prefix",
      cell: (r) => <MonoText>{r.prefix ?? "—"}</MonoText>,
      nowrap: true,
    },
    { id: "scopes", header: "Scopes", cell: (r) => r.scopes ?? "—" },
    {
      id: "status",
      header: "Status",
      cell: (r) => <StatusChip status={r.enabled === false ? "REVOKED" : "ACTIVE"} />,
    },
    { id: "used", header: "Last used", cell: (r) => <RelativeTime value={r.lastUsedAt} /> },
    {
      id: "actions",
      header: "",
      align: "right",
      cell: (r) =>
        r.enabled !== false ? (
          <Button
            size="small"
            color="error"
            onClick={async () => {
              if (
                await confirm({
                  title: `Revoke "${r.name}"?`,
                  destructive: true,
                  confirmLabel: "Revoke",
                })
              ) {
                await m.revoke.mutateAsync(r.id);
                toast.success("Key revoked");
              }
            }}
          >
            Revoke
          </Button>
        ) : null,
    },
  ];

  async function onIssue() {
    const res = await m.issue.mutateAsync({ name, scopes });
    setIssued(res.plaintext);
    setCreating(false);
    setName("");
  }

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ justifyContent: "flex-end" }}>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          New API key
        </Button>
      </Stack>

      {issued ? (
        <Alert severity="success" onClose={() => setIssued(null)}>
          <Stack spacing={1}>
            <span>Copy this now — it is never shown again.</span>
            <CodeBlock code={issued} />
          </Stack>
        </Alert>
      ) : null}

      <Card>
        <DataTable
          columns={columns}
          data={data}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          rowKey={(r) => r.id}
          empty={
            <EmptyState
              title="No API keys"
              description="Issue a key for external systems calling the /v1/external API."
            />
          }
        />
      </Card>

      <Dialog open={creating} onClose={() => setCreating(false)} maxWidth="xs" fullWidth>
        <DialogTitle>New API key</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              size="small"
              label="Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            <TextField
              size="small"
              label="Scopes (comma-separated)"
              value={scopes}
              onChange={(e) => setScopes(e.target.value)}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreating(false)}>Cancel</Button>
          <Button variant="contained" disabled={!name || m.issue.isPending} onClick={onIssue}>
            Issue
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
